import UseCasePage, {
  type UseCaseFaq,
  type UseCaseScene,
  type UseCaseStat,
  type UseCaseWhy,
} from "@/components/public/use-case-page"
import type { SeoMeta } from "@/types"

const WHY: UseCaseWhy[] = [
  {
    title: "New-build launches live and die on reservations",
    description: "A developer launch is judged on how fast the plots are reserved. Social video ads in the two weeks before and after the launch weekend are the single cheapest way to drive those reservations \u2014 especially off-plan.",
  },
  {
    title: "CGIs need lifestyle context to convert",
    description: "A CGI on its own is a drawing. Wrapped in a social video with captions, voiceover, postcode lifestyle cues and pricing, it becomes a story buyers can imagine themselves inside. QuiteRight handles the wrap.",
  },
  {
    title: "Phased releases need repeat marketing",
    description: "A 60-plot development launches over 12\u201318 months in phases. Each phase release needs its own video ad: phase 2 available, phase 3 off-plan, phase 4 register your interest. Reusing one render across phases is false economy.",
  },
  {
    title: "Local boost beats national brand awareness",
    description: "Most buyers of a new development live within 10 miles. A \u00A3100\u2013\u00A3500 Meta boost targeting the local postcodes consistently outperforms a national brand campaign for unit reservations.",
  },
]

const SCENES: UseCaseScene[] = [
  { title: "Launch teaser opener", duration: "0:00\u20130:03", description: "Bold headline \u2014 \u201CNow launching\u201D or \u201CPhase 2 released\u201D \u2014 with the development name. Uses the hero CGI or marquee photo. Builds immediate context in the first three seconds." },
  { title: "Site-wide hero shot", duration: "0:03\u20130:08", description: "Drone frame, master plan, or marquee CGI. Voiceover anchors: location, development name, headline price (\u201Cfrom \u00A3XXXk\u201D)." },
  { title: "Housetype highlights", duration: "0:08\u20130:18", description: "2\u20134 quick cuts through the housetypes being released \u2014 2-bed apartment, 3-bed semi, 4-bed detached \u2014 with pricing ladder. Captions show bedroom count and starting price." },
  { title: "Lifestyle and location cues", duration: "0:18\u20130:25", description: "Local school catchment, station, town centre minutes, green space. Trust-building context for buyers weighing the postcode." },
  { title: "Incentives or Help to Buy", duration: "0:25\u20130:30", description: "Developer incentives \u2014 stamp duty paid, part-exchange, flooring included, or current equivalent schemes. Positioned as the reason to act this week, not next month." },
  { title: "Reserve-now CTA", duration: "0:30\u20130:35", description: "\u201CReserve your plot\u201D with sales centre phone, website URL, and a clear time-bound ask (\u201Cphase 2 selling fast\u201D). Voiceover closes confidently." },
]

const STATS: UseCaseStat[] = [
  { value: "35s", label: "ideal runtime", detail: "Slightly longer than a typical listing ad \u2014 new builds need more lifestyle and price context." },
  { value: "Phased", label: "re-renders", detail: "Phase 1, 2, 3 \u2014 each release gets its own render with updated pricing, housetypes and availability." },
  { value: "CGI+photo", label: "hybrid support", detail: "QuiteRight blends CGIs, site photos and drone footage seamlessly into a single social-first video." },
  { value: "Local", label: "boost ready", detail: "Every render comes in 9:16, 1:1 and 16:9 \u2014 perfect for a local-radius Meta boost campaign." },
]

const FAQ: UseCaseFaq[] = [
  {
    q: "Can QuiteRight work with CGIs instead of real photos?",
    a: "Yes \u2014 CGI is treated the same as any image. Upload the CGIs to the development listing (or point QuiteRight at the developer's own website if the photos are hosted there) and the template sequences them with the correct pacing, captions and voiceover.",
  },
  {
    q: "How do I handle phased releases?",
    a: "Create one QuiteRight listing per phase, or update the existing one at each phase release. The render is cheap and fast \u2014 there's no reason to keep showing \u201Cphase 1\u201D when phase 2 has launched. Each phase gets a fresh video with current pricing and availability.",
  },
  {
    q: "Should the voiceover mention Help to Buy or other schemes?",
    a: "Yes if they apply to your current release. The most effective new-build ads mention one concrete incentive \u2014 stamp duty paid, part-exchange, 5% deposit schemes \u2014 rather than listing all of them. QuiteRight's new-build script template lets you set the headline incentive per render.",
  },
  {
    q: "What about compliance and disclaimers?",
    a: "The voiceover script stays factual and claim-free. Pricing is shown as \u201Cfrom \u00A3X\u201D. Incentives are named without over-promising terms. For specific regulated statements (e.g. financial promotions), your marketing team can swap in your pre-approved copy in the QuiteRight script editor before rendering.",
  },
  {
    q: "Does this work for affordable housing or shared ownership too?",
    a: "Yes. Shared ownership and affordable launches respond particularly well to video because the pricing structure is harder to explain in a photo caption. A 30-second video with voiceover explains the rent-plus-share split clearly, driving qualified enquiries instead of time-wasters.",
  },
]

export default function NewBuildLaunchVideo({ meta }: { meta: SeoMeta }) {
  return (
    <UseCasePage
      meta={meta}
      useCaseName="New Build Launch Video"
      breadcrumbName="New Build Launch Video"
      heroHeadline="A launch video ad for every phase, every release, every postcode"
      heroSubhead="New-build launches are won on reservations, and reservations are won on the strength of the marketing in the two weeks on either side of launch day. QuiteRight renders phase-specific social video ads \u2014 CGI, photo or hybrid \u2014 and publishes them to every channel with a single click."
      whyHeadline="Why new-build launches need video that scales with the phases"
      whySubhead="A launch isn't one moment \u2014 it's a sequence. Each phase release needs its own ad, with current pricing, current housetypes, and current incentives."
      whyItems={WHY}
      anatomyHeadline="Anatomy of a new-build launch video"
      anatomySubhead="The QuiteRight new-build template, built around CGI + lifestyle + incentive + phase availability."
      scenes={SCENES}
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Turn every phase release into a social event"
      ctaBody="One video ad pipeline across every phase, every housetype, every incentive \u2014 rendered and published in minutes, not weeks."
      relatedKey="use-cases/new-build-launch-video"
    />
  )
}
